import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, Edit, Mail, Phone, Sun, Moon, Globe, Settings2 } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { TEST_IDS } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import PlayerAvatar, { AvatarUploadButton } from "@/components/PlayerAvatar";
import { BookingRow, niceDate } from "@/pages/MyBookings";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Profile() {
  const { id } = useParams();
  const { user: authUser, refreshUser } = useAuth();
  const { t, lang, setLang, languageNames } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", preferred_position: "any" });

  const isOwn = !id || id === authUser?.id;
  const userId = id || authUser?.id;

  const loadBookings = useCallback(() => api.get("/bookings").then(r => setBookings(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    api.get(`/users/${userId}`).then(r => {
      setProfile(r.data);
      setForm({ name: r.data.name || "", bio: r.data.bio || "", preferred_position: r.data.preferred_position || "any" });
    }).catch(() => {}).finally(() => setLoading(false));
    if (isOwn) { loadBookings(); api.get("/club").then(r => setClub(r.data)).catch(() => {}); }
  }, [userId, isOwn, loadBookings]);

  const handleSave = async () => {
    try {
      await api.put("/users/profile", form);
      toast.success(t("profile.updated"));
      setEditOpen(false);
      refreshUser();
      const res = await api.get(`/users/${userId}`);
      setProfile(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try { await api.put(`/bookings/${bookingId}/cancel`); toast.success(t("profile.cancelled")); loadBookings(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;
  if (!profile) return <div className="text-center py-20"><p className="text-muted-foreground">{t("profile.not_found")}</p></div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter(b => b.date >= today && b.status !== "cancelled").sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  const past = bookings.filter(b => !upcoming.includes(b));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="profile-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="border-border/50 mb-8">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <div className="relative shrink-0">
                <PlayerAvatar name={profile.name} avatarUrl={profile.avatar_url} size="xl" />
                {isOwn && <AvatarUploadButton onUploaded={(url) => { setProfile(p => ({ ...p, avatar_url: url })); refreshUser(); toast.success(t("profile.avatar")); }} />}
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <h1 className="font-heading font-black text-2xl" data-testid="profile-name">{profile.name}</h1>
                  {profile.role === "admin" && <Badge className="rounded-full text-xs bg-golden/10 text-golden border-golden/30">{t("profile.staff")}</Badge>}
                </div>
                <p className="text-muted-foreground text-sm mb-3">{profile.bio || t("profile.no_bio")}</p>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 text-sm">
                  <Badge variant="secondary" className="rounded-full gap-1"><MapPin size={12} /> {t(`profile.${["left", "right"].includes(profile.preferred_position) ? profile.preferred_position : "any"}`)}</Badge>
                  {isOwn && profile.email && <Badge variant="secondary" className="rounded-full gap-1"><Mail size={12} /> {profile.email}</Badge>}
                  {isOwn && profile.phone && <Badge variant="secondary" className="rounded-full gap-1"><Phone size={12} /> {profile.phone}</Badge>}
                </div>
              </div>
              {isOwn && (
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid={TEST_IDS.PROFILE_EDIT} variant="outline" size="sm" className="rounded-full gap-1.5"><Edit size={14} /> {t("profile.edit")}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{t("profile.edit_title")}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div><Label>{t("profile.name")}</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-xl" data-testid="profile-name-input" /></div>
                      <div><Label>{t("profile.bio")}</Label><Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} className="rounded-xl min-h-[80px]" data-testid="profile-bio-input" /></div>
                      <div>
                        <Label>{t("profile.side")}</Label>
                        <Select value={form.preferred_position} onValueChange={v => setForm(f => ({ ...f, preferred_position: v }))}>
                          <SelectTrigger className="rounded-xl" data-testid="profile-position-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">{t("profile.left")}</SelectItem>
                            <SelectItem value="right">{t("profile.right")}</SelectItem>
                            <SelectItem value="any">{t("profile.any")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button data-testid={TEST_IDS.PROFILE_SAVE} onClick={handleSave} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white">{t("profile.save")}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardContent>
        </Card>

        {isOwn && (
          <Card className="border-border/50 mb-6" data-testid="profile-preferences">
            <CardContent className="p-5 sm:p-6">
              <p className="font-heading font-bold text-base flex items-center gap-2 mb-4"><Settings2 size={16} className="text-coral" /> {t("profile.preferences")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Globe size={12} /> {t("nav.language")}</p>
                  <div className="flex gap-2">
                    {Object.keys(languageNames).map(code => (
                      <button key={code} onClick={() => setLang(code)} className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${lang === code ? "bg-coral text-white" : "bg-muted text-muted-foreground"}`} data-testid={`profile-lang-${code}`}>{languageNames[code]}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">{theme === "dark" ? <Moon size={12} /> : <Sun size={12} />} {t("profile.appearance")}</p>
                  <button onClick={toggleTheme} className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-muted text-sm font-semibold" data-testid="profile-theme-toggle">
                    <span>{theme === "dark" ? t("nav.dark") : t("nav.light")}</span>
                    <span className="text-xs text-coral">{theme === "dark" ? t("nav.light") : t("nav.dark")} →</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue={isOwn ? "bookings" : "matches"}>
          <TabsList className="rounded-full bg-muted/50 p-1 mb-6">
            {isOwn && <TabsTrigger value="bookings" className="rounded-full text-sm" data-testid="profile-tab-bookings">{t("profile.bookings")}</TabsTrigger>}
            <TabsTrigger value="matches" className="rounded-full text-sm" data-testid="profile-tab-matches">{t("profile.matches")}</TabsTrigger>
          </TabsList>

          {isOwn && (
            <TabsContent value="bookings" className="space-y-3">
              {upcoming.length === 0 && <p className="text-center text-muted-foreground py-12">{t("profile.no_upcoming")} <a href="/book" className="text-coral underline">{t("mb.book")}</a></p>}
              {upcoming.map(b => <BookingRow key={b._id} b={b} club={club} onCancel={() => handleCancelBooking(b._id)} />)}
              {past.length > 0 && (
                <>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground pt-6 pb-1">{t("mb.past")}</p>
                  {past.slice(0, 5).map(b => <BookingRow key={b._id} b={b} muted />)}
                </>
              )}
            </TabsContent>
          )}

          <TabsContent value="matches">
            <Card className="border-border/50">
              <CardContent className="p-4 space-y-3">
                {profile.recent_matches?.length > 0 ? (
                  profile.recent_matches.map(m => (
                    <div key={m._id} className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/30" data-testid="recent-match-row">
                      <div>
                        <p className="font-medium text-sm">{niceDate(m.date)}{m.time ? ` · ${m.time}` : ""}</p>
                        <p className="text-xs text-muted-foreground">D&D Padel · {t("book.court")}</p>
                      </div>
                      <Badge className="rounded-full text-xs bg-teal/10 text-teal">{m.mode === "competitive" ? t("profile.match") : t("profile.friendly")}</Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-12">{t("profile.no_matches")}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}
